import { jwtDecode } from "jwt-decode";
import { RelativeUrl } from "./environment";
import { Role } from "../types/auth";
import LocalStorageService from "./storage";
import SecureStorageService from "./secure-storage";
import { BrandStatus } from "../types/brand";
import { parseResponse, setUnauthorizedHandler } from "./response";
import Result, { error } from "../types/result";
import { setRedirectUrl, getFullCurrentUrl } from "./redirect-url";
import { stripAppBasePath, withAppBasePath } from "./app-base-path";
import DataDogManager from "./datadog";
import { LOGOUT_PRESERVED_STORAGE_KEYS, PROJECT_VISITS_STORAGE_KEY } from "../constants/logout-preserved-storage";
const AuthTokenKey = "adora-auth-token";
const RefreshTokenKey = "adora-refresh-token";
const UserKey = "adora-user";

type AuthListener = () => void;
const authListeners = new Set<AuthListener>();

const notifyAuthListeners = () => {
  authListeners.forEach((listener) => listener());
};

export const subscribeToAuth = (listener: AuthListener) => {
  authListeners.add(listener);
  return () => {
    authListeners.delete(listener);
  };
};

const LoginApi = RelativeUrl + "/login";
const RefreshTokenApi = RelativeUrl + "/refresh-token";
const SwitchBrandApi = RelativeUrl + "/switch-brand";
const SocialLoginStartApi = RelativeUrl + "/social-login/start";
const SocialLoginCallbackApi = RelativeUrl + "/social-login/callback";
const SocialLoginChooseApi = RelativeUrl + "/social-login/choose";

/** SessionStorage key holding the `state` of a social login in flight, checked when the provider redirects back. */
const SOCIAL_LOGIN_STATE_KEY = "adora-social-login-state";
/** SessionStorage key holding where to land after the social login in flight, so an abandoned one leaves nothing behind. */
const SOCIAL_LOGIN_REDIRECT_KEY = "adora-social-login-redirect";
/** SessionStorage key holding which provider the social login in flight went to, to say which one didn't come back. */
const SOCIAL_LOGIN_PROVIDER_KEY = "adora-social-login-provider";

const SOCIAL_LOGIN_KEYS = [SOCIAL_LOGIN_STATE_KEY, SOCIAL_LOGIN_REDIRECT_KEY, SOCIAL_LOGIN_PROVIDER_KEY];

/**
 * The provider of an unfinished social login found on this page load. Kept here rather
 * than in component state so a login page that remounts still reports it.
 */
let unfinishedSocialLogin: SocialLoginProvider | null = null;

const clearSocialLoginInFlight = () => {
  SOCIAL_LOGIN_KEYS.forEach((key) => window.sessionStorage.removeItem(key));
};

export type SocialLoginProvider = "google" | "microsoft";

interface SocialLoginStartResponse {
  authorization_url: string;
  state: string;
}

export interface SocialLoginAccount {
  user_id: number;
  username: string;
  brand_names: string[];
}

interface SocialLoginResponse extends Partial<LoginResponse> {
  accounts?: SocialLoginAccount[];
  session_token?: string;
}

/**
 * A social login either signs in, or, when the email belongs to several accounts, asks which one.
 * `redirectUrl` is where the sign-in was started to go, if anywhere.
 */
export type SocialLoginOutcome = { redirectUrl: string | null } & (
  | { kind: "signedIn"; user: AdoraUser }
  | { kind: "chooseAccount"; sessionToken: string; accounts: SocialLoginAccount[] }
);

/**
 * POSTs to an unauthenticated social-login endpoint and reads the reply. Anything
 * that throws on the way (network failure, a non-JSON body, a malformed token)
 * comes back as an error Result, so callers never see a rejected promise.
 */
const postSocialLogin = async <T, R>(url: string, body: object, read: (value: T) => R): Promise<Result<R>> => {
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body)
    });
    const result = await parseResponse<T>(response);
    return result.map(read);
  } catch {
    return error({ tag: "unknownError", message: "Sign-in could not be completed. Please try again." });
  }
};

/** The exchange in flight for a provider code. A code is single-use, so a page that mounts twice must share one exchange. */
let socialLoginExchange: { code: string; result: Promise<Result<SocialLoginOutcome>> } | null = null;

const exchangeSocialLoginCode = async (code: string, state: string): Promise<Result<SocialLoginOutcome>> => {
  const expectedState = window.sessionStorage.getItem(SOCIAL_LOGIN_STATE_KEY);
  const redirectUrl = window.sessionStorage.getItem(SOCIAL_LOGIN_REDIRECT_KEY);
  clearSocialLoginInFlight();
  if (!expectedState || expectedState !== state) {
    return error({
      tag: "codedError",
      code: "invalid_state",
      message: "This sign-in link is not valid. Please try again.",
      detail: {}
    });
  }

  return postSocialLogin(SocialLoginCallbackApi, { code, state }, (value: SocialLoginResponse): SocialLoginOutcome => {
    if (value.accounts && value.session_token) {
      return { kind: "chooseAccount", sessionToken: value.session_token, accounts: value.accounts, redirectUrl };
    }
    return { kind: "signedIn", user: decodeToken(value as LoginResponse), redirectUrl };
  });
};

export const LOGOUT_KEY = "adora-logout";
export const AUTH_RESYNC_EVENT = "adora:auth-resynced";
export const LOGOUT_STARTED_EVENT = "adora:logout-started";
export const LOGOUT_IN_PROGRESS_KEY = "adora-logout-in-progress";

/** SessionStorage key set by web app before full-page navigate on brand switch. Cleared after app-sidebar runs post-switch redirect check (redirect to /home if path not in nav). */
export const BRAND_SWITCH_PENDING_KEY = "adora-brand-switch-pending";

export const TAB_ID = crypto.randomUUID();

const setAuthToken = (token: string) => {
  SecureStorageService.setItem(AuthTokenKey, token);
};

const setRefreshToken = (token: string) => {
  SecureStorageService.setItem(RefreshTokenKey, token);
};

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  tokenType: "bearer";
  brand_status: BrandStatus;
}

export interface RefreshAuthPayload {
  brandId?: number;
  redirectPath?: string;
  trigger?: "manual" | "deep-link";
  sentAt?: number;
}

export interface ExternalInfo {
  role?: string;
  is_agency?: boolean;
  seniority?: string;
}

export interface AdoraUser {
  id: string;
  name: string;
  brandId: number;
  role: Role;
  expiration: number;
  version: number;
  brandStatus: BrandStatus;
  external_info?: ExternalInfo;
}

export interface AdoraInternalUser {
  id: string;
  role: Role;
  expiration: number;
  version: number;
}

interface JWTPayload {
  sub: string;
  brand_id: number;
  exp: number;
  user_role: Role;
  user_name: string;
  external_info?: ExternalInfo;
}

const LatestVersion = 5;
let cachedUserStorageValue: string | null = null;
let cachedAdoraUser: AdoraUser | null = null;
let logoutInProgress = false;
let logoutResetTimer: number | null = null;

const setLogoutInProgress = (nextValue: boolean) => {
  logoutInProgress = nextValue;

  if (typeof window === "undefined") {
    return;
  }

  if (logoutResetTimer !== null) {
    window.clearTimeout(logoutResetTimer);
    logoutResetTimer = null;
  }

  if (!nextValue) {
    window.sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
    return;
  }

  window.sessionStorage.setItem(LOGOUT_IN_PROGRESS_KEY, "true");
  window.dispatchEvent(new Event(LOGOUT_STARTED_EVENT));
  logoutResetTimer = window.setTimeout(() => {
    logoutInProgress = false;
    logoutResetTimer = null;
    window.sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
  }, 5000);
};

export const isLogoutInProgress = () => logoutInProgress;

const getBrandSwitchContext = (targetBrandId: number, payload?: RefreshAuthPayload) => ({
  source_brand_id: getAdoraUser()?.brandId ?? null,
  target_brand_id: targetBrandId,
  trigger: payload?.trigger ?? "manual",
  has_redirect_path: Boolean(payload?.redirectPath)
});

const setAdoraUser = (
  user: string,
  name: string,
  brandId: number,
  role: Role,
  expiration: number,
  brandStatus: BrandStatus,
  external_info?: ExternalInfo
): AdoraUser => {
  setLogoutInProgress(false);
  const adoraUser: AdoraUser = {
    id: user,
    name,
    brandId,
    role,
    expiration,
    version: LatestVersion,
    brandStatus,
    external_info
  };
  const serializedUser = JSON.stringify(adoraUser);
  cachedUserStorageValue = serializedUser;
  cachedAdoraUser = adoraUser;
  LocalStorageService.setItem(UserKey, serializedUser);
  notifyAuthListeners();
  return adoraUser;
};

export const getAdoraUser = () => {
  const user = LocalStorageService.getItem(UserKey);
  if (user === cachedUserStorageValue) {
    return cachedAdoraUser;
  }

  if (!user) {
    cachedUserStorageValue = null;
    cachedAdoraUser = null;
    return null;
  }

  const adoraUser = JSON.parse(user) as AdoraUser;
  if (adoraUser.version === LatestVersion) {
    cachedUserStorageValue = user;
    cachedAdoraUser = adoraUser;
    return adoraUser;
  }

  cachedUserStorageValue = null;
  cachedAdoraUser = null;
  Auth.logout();
  return null;
};

export const syncAuthFromStorage = () => {
  cachedUserStorageValue = null;
  cachedAdoraUser = null;

  const user = getAdoraUser();
  notifyAuthListeners();

  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTH_RESYNC_EVENT));
  }

  return user;
};

/** Returns true when the user has internal role (same meaning as useIsInternalUser in the web app). */
export const isInternalUser = (user: AdoraUser | null): boolean => user?.role === "internal";

export const isActiveBrand = (user: AdoraUser) => {
  const brandStatus = user.brandStatus;

  // TODO: Remove this once BE changes are complete. Brand Status should always have a value
  if (brandStatus === undefined) {
    return true;
  }

  return brandStatus === "active" || brandStatus === "trial";
};

const decodeToken = (response: LoginResponse) => {
  const refreshToken = response.refresh_token;
  if (refreshToken) {
    setRefreshToken(refreshToken);
  }

  const token = response.access_token;

  if (!token) {
    throw "No token found in response";
  }

  setAuthToken(token);
  const decodedToken = jwtDecode<JWTPayload>(token);
  return setAdoraUser(
    decodedToken.sub,
    decodedToken.user_name,
    decodedToken.brand_id,
    decodedToken.user_role,
    decodedToken.exp,
    response.brand_status,
    decodedToken.external_info
  );
};

let refreshTokenPromise: Promise<AdoraUser | null> | null = null;

export const redirectToLogin = (includeRedirectUrl: boolean = true, redirectUrl?: string | null) => {
  const loginPath = withAppBasePath("/login");
  if (window.location.pathname === loginPath) return;
  const fullUrl = stripAppBasePath(window.location.pathname) + window.location.search + window.location.hash;
  const url = loginPath + (includeRedirectUrl ? "?redirect_url=" + encodeURIComponent(redirectUrl || fullUrl) : "");
  // eslint-disable-next-line no-restricted-syntax -- full reload on purpose: drops in-memory auth state; url already carries the basename
  window.location.href = url;
};

const refreshAccessToken = async () => {
  const refreshToken = SecureStorageService.getItem(RefreshTokenKey);
  const currentUser = getAdoraUser();

  const response = await fetch(RefreshTokenApi, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify({
      refresh_token: refreshToken,
      brand_id: currentUser?.brandId // Include current brand_id to preserve context
    })
  });
  // Only a 401 means the refresh token is dead; anything else may pass, so it is not remembered.
  if (!response.ok && response.status !== 401) {
    throw new Error(`Token refresh failed: ${response.status}`);
  }

  const result = await parseResponse<LoginResponse>(response);

  return result.match(
    () => null,
    (value) => decodeToken(value)
  );
};

const REFRESH_BUFFER_SECONDS = 60;

/** The refresh token the server last refused an early refresh for, so later sends skip that round trip. */
let earlyRefreshRefusedFor: string | null = null;

const isExpiringSoon = (user: AdoraUser, minSecondsLeft: number) =>
  user.expiration < Date.now() / 1000 + Math.max(minSecondsLeft, REFRESH_BUFFER_SECONDS);

/**
 * Refreshes the access token while holding a lock shared by every tab. The backend
 * rotates a single refresh token per user, so a second tab sending the old one would
 * be rejected and log every tab out; instead it waits, then reuses the first tab's token.
 */
const refreshAccessTokenAcrossTabs = async (minSecondsLeft: number): Promise<AdoraUser | null> => {
  if (typeof navigator === "undefined" || !navigator.locks) {
    return refreshAccessToken();
  }

  return navigator.locks.request("adora-token-refresh", async () => {
    const user = getAdoraUser();
    if (user && !isExpiringSoon(user, minSecondsLeft)) {
      return user;
    }
    return refreshAccessToken();
  });
};

const Auth = {
  login: async (formData: FormData): Promise<Result<AdoraUser>> => {
    const requestOptions = {
      method: "POST",
      body: formData
    };

    const response = await fetch(LoginApi, requestOptions);
    const result = await parseResponse<LoginResponse>(response);

    return result.map(decodeToken);
  },
  /** Returns the provider sign-in URL to navigate to, remembering its `state` and `redirectUrl` for the callback. */
  startSocialLogin: (provider: SocialLoginProvider, redirectUrl?: string | null): Promise<Result<string>> =>
    postSocialLogin(SocialLoginStartApi, { provider }, ({ authorization_url, state }: SocialLoginStartResponse) => {
      unfinishedSocialLogin = null;
      window.sessionStorage.setItem(SOCIAL_LOGIN_STATE_KEY, state);
      window.sessionStorage.setItem(SOCIAL_LOGIN_PROVIDER_KEY, provider);
      if (redirectUrl) {
        window.sessionStorage.setItem(SOCIAL_LOGIN_REDIRECT_KEY, redirectUrl);
      } else {
        window.sessionStorage.removeItem(SOCIAL_LOGIN_REDIRECT_KEY);
      }
      return authorization_url;
    }),
  /** Finishes a social login on the provider's redirect back. A `state` this tab did not start is refused. */
  completeSocialLogin: (code: string, state: string): Promise<Result<SocialLoginOutcome>> => {
    if (socialLoginExchange?.code !== code) {
      socialLoginExchange = { code, result: exchangeSocialLoginCode(code, state) };
    }
    return socialLoginExchange.result;
  },
  /**
   * Ends a social login this tab started that never completed, such as one a provider
   * refused on its own page, and returns which provider it went to. Keeps returning it
   * for the rest of this page load, so a remount reports it too; a new sign-in or
   * `abandonSocialLogin` forgets it. Returns null when there is none.
   */
  takeUnfinishedSocialLogin: (): SocialLoginProvider | null => {
    if (window.sessionStorage.getItem(SOCIAL_LOGIN_STATE_KEY)) {
      const provider = window.sessionStorage.getItem(SOCIAL_LOGIN_PROVIDER_KEY);
      unfinishedSocialLogin = provider === "google" || provider === "microsoft" ? provider : null;
    }
    clearSocialLoginInFlight();
    return unfinishedSocialLogin;
  },
  /** Ends the social login in flight without reporting it, for a page that explains the failure itself. */
  abandonSocialLogin: () => {
    unfinishedSocialLogin = null;
    clearSocialLoginInFlight();
  },
  /** Signs in as the account picked from those a social login offered. */
  chooseSocialLoginAccount: (sessionToken: string, userId: number): Promise<Result<AdoraUser>> =>
    postSocialLogin(SocialLoginChooseApi, { session_token: sessionToken, user_id: userId }, decodeToken),
  logout: (redirect?: boolean | "preserve-redirect") => {
    setLogoutInProgress(true);

    // Capture URL BEFORE clearing (in case we need it)
    const currentUrl = redirect === "preserve-redirect" ? getFullCurrentUrl() : null;

    // Tour completion and the home page's project trail are kept through logout, so returning users
    // neither see first-use education again nor lose where they left off after a session expires;
    // session data is still cleared. `signOut` drops the trail too.
    SecureStorageService.clear(LOGOUT_PRESERVED_STORAGE_KEYS);
    cachedUserStorageValue = null;
    cachedAdoraUser = null;
    LocalStorageService.setItem(LOGOUT_KEY, Math.random().toString());

    // Save redirect URL AFTER clearing storage (so it doesn't get wiped)
    if (currentUrl) {
      setRedirectUrl(currentUrl);
    }

    if (redirect) {
      redirectToLogin(false);
    }
  },
  /** Signs out at the person's request: unlike a session expiring, this also forgets the home page's project trail. */
  signOut: () => {
    LocalStorageService.removeItem(PROJECT_VISITS_STORAGE_KEY);
    Auth.logout(true);
  },
  /** `minSecondsLeft`: refresh early for a request whose work outlives it, like an agent turn. */
  getBearerToken: async (minSecondsLeft: number = REFRESH_BUFFER_SECONDS): Promise<string> => {
    const user = getAdoraUser();
    const authToken = SecureStorageService.getItem(AuthTokenKey);

    if (!user || !authToken) {
      Auth.logout("preserve-redirect");
      return "";
    }

    if (!isExpiringSoon(user, minSecondsLeft)) {
      return `Bearer ${authToken}`;
    }

    const fallback = isExpiringSoon(user, REFRESH_BUFFER_SECONDS) ? null : `Bearer ${authToken}`;
    const refreshToken = SecureStorageService.getItem(RefreshTokenKey);
    if (fallback && refreshToken && refreshToken === earlyRefreshRefusedFor) {
      return fallback;
    }
    try {
      // Only allow one refresh token request at a time
      if (!refreshTokenPromise) {
        refreshTokenPromise = refreshAccessTokenAcrossTabs(minSecondsLeft);
      }

      const refreshedUser = await refreshTokenPromise;

      if (!refreshedUser) {
        if (fallback) {
          earlyRefreshRefusedFor = refreshToken;
          return fallback;
        }
        Auth.logout("preserve-redirect");
        return "";
      }

      const newToken = SecureStorageService.getItem(AuthTokenKey);
      return newToken ? `Bearer ${newToken}` : "";
    } catch {
      if (fallback) return fallback;
      Auth.logout("preserve-redirect");
      return "";
    } finally {
      refreshTokenPromise = null;
    }
  },
  switchBrand: async (brandId: number, payload?: RefreshAuthPayload): Promise<Result<AdoraUser>> => {
    const brandSwitchContext = getBrandSwitchContext(brandId, payload);
    DataDogManager.reportCustomAction("brand_switch_started", brandSwitchContext);

    try {
      const response = await fetch(SwitchBrandApi, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: await Auth.getBearerToken()
        },
        body: JSON.stringify({
          brand_id: brandId
        })
      });

      const result = await parseResponse<LoginResponse>(response);
      if (result.isError()) {
        DataDogManager.reportCustomAction("brand_switch_failed", {
          ...brandSwitchContext,
          failure_stage: "switch_brand_api"
        });
        return result.map(decodeToken);
      }

      SecureStorageService.clearAuthKeys();
      const updatedResult = result.map(decodeToken);
      Auth.refreshOtherTabs({ brandId, ...payload });
      DataDogManager.reportCustomAction("brand_switch_completed", brandSwitchContext);
      return updatedResult;
    } catch (error) {
      DataDogManager.reportCustomAction("brand_switch_failed", {
        ...brandSwitchContext,
        failure_stage: error instanceof Error ? error.name : "unknown"
      });
      throw error;
    }
  },
  refreshOtherTabs: (payload?: RefreshAuthPayload) => {
    const nextPayload = {
      ...payload,
      sentAt: payload?.sentAt ?? Date.now()
    };

    DataDogManager.reportCustomAction("auth_refresh_broadcast_sent", {
      target_brand_id: nextPayload.brandId ?? null,
      trigger: nextPayload.trigger ?? "manual",
      has_redirect_path: Boolean(nextPayload.redirectPath)
    });

    const channel = new BroadcastChannel("auth-refresh");
    channel.postMessage({ type: "REFRESH_AUTH", ...nextPayload, senderId: TAB_ID });
    channel.close();
  }
};

// Auto-logout when the backend signals the session is dead.
// Backend tags such 401s with `WWW-Authenticate: Bearer`; per-request authz
// failures (wrong brand, login fail, OTC, etc.) intentionally do not.
setUnauthorizedHandler((response) => {
  if (isLogoutInProgress()) return;

  const wwwAuth = response.headers.get("WWW-Authenticate") ?? "";
  if (!wwwAuth.toLowerCase().startsWith("bearer")) return;

  // Endpoints that own their own 401 handling.
  const ownHandling = [RefreshTokenApi, LoginApi, SocialLoginStartApi, SocialLoginCallbackApi, SocialLoginChooseApi];
  if (ownHandling.some((api) => response.url.endsWith(api))) return;

  // Avoid redirect loops if a 401 fires while already on /login.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/login")) return;

  Auth.logout("preserve-redirect");
});

export default Auth;
